import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FleetSharesLogo } from '../components/Logo';

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // If already logged in, redirect to dashboard
    if (user) {
      navigate('/');
      return;
    }

    // Check for SSO token in URL
    const token = searchParams.get('token');
    if (token) {
      handleSSOLogin(token);
    }
  }, [user, searchParams, navigate]);

  const handleSSOLogin = async (token) => {
    setLoading(true);
    setError('');
    try {
      await login(token);
      navigate('/');
    } catch (err) {
      setError('Invalid session token. Please try again.');
      console.error('SSO login failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = () => {
    // For demo/testing: simulate a token
    const demoToken = 'demo_token_123';
    handleSSOLogin(demoToken);
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8 flex flex-col items-center">
          <FleetSharesLogo size="hero" />
          <h1 className="text-4xl font-bold text-[hsl(var(--brand-primary))] mt-6 mb-2" style={{ fontFamily: 'Manrope' }}>
            Fleet Shares
          </h1>
          <p className="text-[hsl(var(--text-secondary))]">
            Employee Truck & Trailer Ownership Portal
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-semibold text-[hsl(var(--text-primary))] mb-6 text-center">
            Sign In with HUB SSO
          </h2>

          {error && (
            <div className="mb-6 p-4 bg-[hsl(var(--loss-light))] border border-[hsl(var(--loss))] rounded-lg">
              <p className="text-sm text-[hsl(var(--loss))]">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <button
              data-testid="sso-login-button"
              onClick={() => {
                // Redirect to HUB SSO with callback URL
                const callbackUrl = encodeURIComponent(`${window.location.origin}/auth/callback`);
                window.location.href = `https://yourultimatehub.com/sso/authorize?app_id=fleet_share&redirect_uri=${callbackUrl}`;
              }}
              disabled={loading}
              className="w-full bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary-dark))] text-white font-semibold px-6 py-4 rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md disabled:opacity-50"
            >
              {loading ? 'Signing In...' : 'Continue with HUB SSO'}
            </button>

            {/* Demo Login for Testing */}
            <button
              data-testid="demo-login-button"
              onClick={handleDemoLogin}
              disabled={loading}
              className="w-full bg-white hover:bg-[hsl(var(--background-secondary))] text-[hsl(var(--text-primary))] font-medium px-6 py-3 rounded-lg border border-[hsl(var(--border))] transition-colors duration-200 disabled:opacity-50"
            >
              Demo Login (Testing)
            </button>
            
            <button
              data-testid="admin-login-button"
              onClick={() => handleSSOLogin('admin_token_123')}
              disabled={loading}
              className="w-full bg-white hover:bg-[hsl(var(--background-secondary))] text-[hsl(var(--text-primary))] font-medium px-6 py-3 rounded-lg border border-[hsl(var(--border))] transition-colors duration-200 disabled:opacity-50"
            >
              Admin Login (Testing)
            </button>
          </div>

          <p className="text-xs text-[hsl(var(--text-tertiary))] mt-6 text-center">
            Only authorized HUB users can access this portal.
          </p>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-[hsl(var(--text-tertiary))] mt-8">
          Internal use only. All access is logged and monitored.
        </p>
      </div>
    </div>
  );
}